/**
 * Scene Selector panel component
 * Allows placing framed artwork into interior scene backgrounds
 */

import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, Slider, CollapsibleCard, Switch, cn } from '@frameit/ui';
import { useProjectStore, useSelectedArtwork } from '../../stores/projectStore';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  SCENES,
  SCENE_CATEGORIES,
  getSceneAngleVariants,
  getUniqueScenes,
} from '../../data/scenes';
import type { SceneCategory } from '@frameit/types';

// Scenes and categories imported from ../../data/scenes

export interface SceneSelectorProps {
  className?: string;
}

/** Scene selector component */
export function SceneSelector({ className }: SceneSelectorProps) {
  const selectedArtwork = useSelectedArtwork();
  const project = useProjectStore((s) => s.project);
  const setScene = useProjectStore((s) => s.setScene);
  const updateScenePlacement = useProjectStore((s) => s.updateScenePlacement);
  const sceneZoom = useProjectStore((s) => s.sceneZoom);
  const setSceneZoom = useProjectStore((s) => s.setSceneZoom);

  const [category, setCategory] = useState<SceneCategory | 'all'>('all');
  const [search, setSearch] = useState('');
  const isMobile = useIsMobile();

  // Check if scene mode is enabled
  const sceneEnabled = !!project?.scene;
  const currentSceneId = project?.scene?.templateId;
  const currentPlacement = project?.scene?.placements.find(
    (p) => p.framedArtworkId === selectedArtwork?.id,
  );

  // Filter scenes by category and search (show unique per group)
  const searchLower = search.toLowerCase().trim();
  const uniqueScenes = getUniqueScenes();
  const filteredScenes = uniqueScenes.filter((s) => {
    const matchesCategory = category === 'all' || s.category === category;
    const matchesSearch =
      !searchLower ||
      s.name.toLowerCase().includes(searchLower) ||
      s.category.includes(searchLower);
    return matchesCategory && matchesSearch;
  });

  // Get angle variants for currently selected scene
  const currentScene = currentSceneId ? SCENES.find((s) => s.id === currentSceneId) : null;
  const angleVariants = currentScene ? getSceneAngleVariants(currentScene.id) : [];
  const hasMultipleAngles = angleVariants.length > 1;
  // Check if any scene in the current group is selected
  const currentGroupId = currentScene?.groupId;

  // Default placement for artwork
  const defaultPlacement = {
    framedArtworkId: selectedArtwork?.id || '',
    zoneId: 'main',
    scale: 0.4,
    offset: { x: 0, y: 0 },
  };

  // Get effective placement (use current or default)
  const effectivePlacement = currentPlacement || defaultPlacement;

  // Handle scene toggle
  const handleToggle = (enabled: boolean) => {
    if (enabled && selectedArtwork) {
      // Enable scene with first preset
      setScene({
        templateId: SCENES[0].id,
        placements: [
          {
            framedArtworkId: selectedArtwork.id,
            zoneId: 'main',
            scale: 0.4,
            offset: { x: 0, y: 0 },
          },
        ],
      });
    } else {
      // Disable scene
      setScene(undefined);
    }
  };

  // Handle scene selection
  const handleSceneSelect = (sceneId: string) => {
    if (!selectedArtwork) return;

    // Keep existing placements or create new one for current artwork
    const existingPlacements = project?.scene?.placements || [];
    const hasPlacement = existingPlacements.some((p) => p.framedArtworkId === selectedArtwork.id);

    const placements = hasPlacement
      ? existingPlacements
      : [
          ...existingPlacements,
          {
            framedArtworkId: selectedArtwork.id,
            zoneId: 'main',
            scale: 0.4,
            offset: { x: 0, y: 0 },
          },
        ];

    setScene({
      templateId: sceneId,
      placements,
    });
  };

  // Ensure placement exists before updating
  const ensurePlacementExists = () => {
    if (!selectedArtwork || !project?.scene) return;
    if (currentPlacement) return; // Already exists

    // Add placement for this artwork
    setScene({
      ...project.scene,
      placements: [
        ...project.scene.placements,
        {
          framedArtworkId: selectedArtwork.id,
          zoneId: 'main',
          scale: 0.4,
          offset: { x: 0, y: 0 },
        },
      ],
    });
  };

  // Handle placement updates
  const handleScaleChange = (scale: number) => {
    if (!selectedArtwork || !project?.scene) return;
    ensurePlacementExists();
    updateScenePlacement(selectedArtwork.id, { scale });
  };

  const handleOffsetXChange = (x: number) => {
    if (!selectedArtwork || !project?.scene) return;
    ensurePlacementExists();
    updateScenePlacement(selectedArtwork.id, {
      offset: { ...effectivePlacement.offset, x },
    });
  };

  const handleOffsetYChange = (y: number) => {
    if (!selectedArtwork || !project?.scene) return;
    ensurePlacementExists();
    updateScenePlacement(selectedArtwork.id, {
      offset: { ...effectivePlacement.offset, y },
    });
  };

  if (!selectedArtwork) {
    return (
      <div className={cn('p-4 text-center text-muted-foreground', className)}>
        Select an artwork to place it in a scene
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Scene toggle */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Interior Scene</h3>
            <p className="text-xs text-muted-foreground">Place your artwork in a room setting</p>
          </div>
          <Switch checked={sceneEnabled} onCheckedChange={handleToggle} />
        </div>
      </div>

      {/* Scene browser + controls in single scroll container */}
      <div
        className={cn('flex-1 overflow-auto', !sceneEnabled && 'opacity-50 pointer-events-none')}
      >
        <div className="p-4">
          {/* Search */}
          <div className="relative mb-3">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search scenes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-8 pl-8 pr-3 text-sm rounded-md border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Category tabs */}
          <Tabs
            value={category}
            onValueChange={(v) => setCategory(v as SceneCategory | 'all')}
            className="mb-4"
          >
            <TabsList className="flex-wrap h-auto gap-1">
              {SCENE_CATEGORIES.map((cat) => (
                <TabsTrigger key={cat.id} value={cat.id} className="text-xs">
                  {cat.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Scene grid */}
          <div className="grid grid-cols-2 gap-3">
            {filteredScenes.map((scene) => {
              const isSelected =
                currentSceneId === scene.id || (currentGroupId && scene.groupId === currentGroupId);

              return (
                <button
                  key={scene.id}
                  onClick={() => handleSceneSelect(scene.id)}
                  className={cn(
                    'relative aspect-[4/3] rounded-lg overflow-hidden border-2 transition-all',
                    'hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary',
                    isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-transparent',
                  )}
                >
                  {/* Room thumbnail */}
                  <img
                    src={scene.thumbnail}
                    alt={scene.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  {/* Scene name */}
                  <div className="absolute bottom-0 inset-x-0 bg-black/50 px-2 py-1">
                    <span className="text-xs text-white">{scene.name}</span>
                  </div>

                  {/* Multi-angle badge */}
                  {scene.groupId && getSceneAngleVariants(scene.id).length > 1 && (
                    <div className="absolute top-1 left-1 bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                      {getSceneAngleVariants(scene.id).length} views
                    </div>
                  )}

                  {/* Premium badge */}
                  {scene.isPremium && (
                    <div className="absolute top-1 right-1 bg-yellow-500 text-yellow-900 text-[10px] font-bold px-1.5 py-0.5 rounded">
                      PRO
                    </div>
                  )}

                  {/* Selected checkmark */}
                  {isSelected && (
                    <div
                      className={cn(
                        'absolute w-5 h-5 bg-primary rounded-full flex items-center justify-center',
                        scene.groupId ? 'top-1 left-[52px]' : 'top-1 left-1',
                      )}
                    >
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Angle variants for selected scene */}
          {hasMultipleAngles && (
            <div className="mt-3 p-3 bg-muted/30 rounded-lg">
              <label className="text-xs text-muted-foreground mb-2 block">Camera Angle</label>
              <div className="flex gap-2">
                {angleVariants.map((variant) => (
                  <button
                    key={variant.id}
                    onClick={() => handleSceneSelect(variant.id)}
                    className={cn(
                      'flex-1 px-3 py-1.5 text-xs rounded-md border transition-colors',
                      currentSceneId === variant.id
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:bg-accent',
                    )}
                  >
                    {variant.angleLabel ?? variant.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Placement controls */}
        <CollapsibleCard
          title="Scene Settings"
          collapsible={isMobile}
          className="mx-4 mb-4"
          disabled={!sceneEnabled}
        >
          {/* Scene Zoom */}
          <div>
            <label className="text-xs text-muted-foreground">Room Zoom</label>
            <Slider
              value={sceneZoom}
              min={50}
              max={200}
              step={5}
              onChange={setSceneZoom}
              showValue
              formatValue={(v) => `${v}%`}
            />
          </div>

          <h4 className="text-sm font-medium pt-2">Artwork</h4>

          {/* Scale */}
          <div>
            <label className="text-xs text-muted-foreground">Size</label>
            <Slider
              value={effectivePlacement.scale}
              min={0.1}
              max={0.8}
              step={0.01}
              onChange={handleScaleChange}
              showValue
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />
          </div>

          {/* Horizontal position */}
          <div>
            <label className="text-xs text-muted-foreground">Horizontal Position</label>
            <Slider
              value={effectivePlacement.offset.x}
              min={-1.5}
              max={1.5}
              step={0.01}
              onChange={handleOffsetXChange}
              showValue
              formatValue={(v) => `${v > 0 ? '+' : ''}${Math.round(v * 100)}%`}
            />
          </div>

          {/* Vertical position */}
          <div>
            <label className="text-xs text-muted-foreground">Vertical Position</label>
            <Slider
              value={effectivePlacement.offset.y}
              min={-1.0}
              max={1.0}
              step={0.01}
              onChange={handleOffsetYChange}
              showValue
              formatValue={(v) => `${v > 0 ? '+' : ''}${Math.round(v * 100)}%`}
            />
          </div>
        </CollapsibleCard>
      </div>
    </div>
  );
}
